import store from '@/store/index'
import { saveAs } from 'file-saver'
import moment from 'moment'
import {
  Firestore,
  Database,
  NotificationsDoc,
  SignaturesDoc,
  HistoryCollection,
  HistoryDoc,
  UsersDoc,
} from '@/firebase-exports'
import i18n from '@/i18n'

/**
 * Devuelve el estado de alerta para una notificación o firma electrónica, según la fecha de emisión.
 * @param {Object} date - La fecha de la Alerta.
 * @returns {Object} - Un objeto que contiene la información del estado de alerta.
 */
export function getAlertStatus(alert) {
  const currentDate = Date.now()
  if (!alert) {
    return {
      color: 'warningLow',
      icon: 'mdi-bell-outline',
      status: '',
    }
  }
  // Si la fecha de hoy es posterior a la emision + 10 dias es que ha expirado
  else if (
    currentDate > new Date(alert.date).setDate(new Date(alert.date).getDate())
  ) {
    return {
      color: 'error',
      icon: 'mdi-bell-alert',
      status: 'alertStatus.expired',
    }
  } else if (
    currentDate <
      new Date(alert.date).setDate(new Date(alert.date).getDate()) &&
    new Date(alert.date).setDate(new Date(alert.date).getDate() - 3) <
      currentDate
  ) {
    return {
      color: 'warning',
      icon: 'mdi-bell-ring',
      status: 'alertStatus.urgent',
    }
  } else {
    return {
      color: 'accept',
      icon: 'mdi-bell',
      status: 'alertStatus.pending',
    }
  }
}

/**
 * Crea una alerta para una notificación o firma electrónica.
 * @param {string} assetId - El ID del Asset.
 * @param {Object} alert - El objeto de alerta.
 * @param {string} objectType - El tipo de objeto (notificación o firma electrónica).
 * @returns {Promise} - Una promesa que se resuelve cuando la operación ha finalizado.
 */
export async function alertCreate(assetId, alert, objectType) {
  // Validar que se haya proporcionado un ID de notificación
  if (!assetId || typeof assetId !== 'string') {
    throw new Error('El ID de la notificación es inválido')
  }

  // Validar que se haya proporcionado un objeto de alerta
  if (!alert || typeof alert !== 'object') {
    throw new Error('El objeto de alerta es inválido')
  }

  // Validar que se haya proporcionado un tipo de objeto válido
  if (!['notifications', 'signatures'].includes(objectType)) {
    throw new Error('El tipo de objeto es inválido')
  }
  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  if (objectType === 'notifications') {
    batch.update(NotificationsDoc(userId, assetId), { alert })
  } else if (objectType === 'signatures') {
    batch.update(SignaturesDoc(userId, assetId), { alert })
  }

  batch.set(
    HistoryDoc(
      userId,
      objectType,
      assetId,
      Firestore.doc(HistoryCollection(userId, assetId, objectType)).id
    ),
    {
      actionType: 'alertCreate',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
      ...alert,
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Modifica una alerta para una notificación o firma electrónica.
 * @param {string} assetId - El ID de asset.
 * @param {Object} alert - El objeto de alerta.
 * @param {string} objectType - El tipo de objeto (notificación o firma electrónica).
 * @returns {Promise} - Una promesa que se resuelve cuando la operación ha finalizado.
 */
export async function alertUpdate(assetId, alert, objectType) {
  // Validar que se haya proporcionado un ID de notificación
  if (!assetId || typeof assetId !== 'string') {
    throw new Error('El ID de la notificación es inválido')
  }

  // Validar que se haya proporcionado un objeto de alerta
  if (!alert || typeof alert !== 'object') {
    throw new Error('El objeto de alerta es inválido')
  }

  // Validar que se haya proporcionado un tipo de objeto válido
  if (!['notifications', 'signatures'].includes(objectType)) {
    throw new Error('El tipo de objeto es inválido')
  }

  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  if (objectType === 'notifications') {
    batch.update(NotificationsDoc(userId, assetId), { alert })
  } else if (objectType === 'signatures') {
    batch.update(SignaturesDoc(userId, assetId), { alert })
  }

  batch.set(
    HistoryDoc(
      userId,
      objectType,
      assetId,
      Firestore.doc(HistoryCollection(userId, objectType, assetId)).id
    ),
    {
      actionType: 'alertModify',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
      ...alert,
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Marca una alerta como completada para una notificación o firma electrónica.
 * @param {string} assetId - El ID del Asset.
 * @param {Object} alert - El objeto de alerta.
 * @param {string} objectType - El tipo de objeto (notificación o firma electrónica).
 * @returns {Promise} - Una promesa que se resuelve cuando la operación ha finalizado.
 */
export async function alertDone(assetId, alert, objectType) {
  // Validar que se haya proporcionado un ID de notificación
  if (!assetId || typeof assetId !== 'string') {
    throw new Error('El ID de la notificación es inválido')
  }

  // Validar que se haya proporcionado un objeto de alerta
  if (!alert || typeof alert !== 'object') {
    throw new Error('El objeto de alerta es inválido')
  }

  // Validar que se haya proporcionado un tipo de objeto válido
  if (!['notifications', 'signatures'].includes(objectType)) {
    throw new Error('El tipo de objeto es inválido')
  }

  const userId = store.state.user.parentRef
    ? store.state.user.parentRef.id
    : store.state.user.id

  const batch = Firestore.writeBatch(Database)

  if (objectType === 'notifications') {
    batch.update(NotificationsDoc(userId, assetId), {
      alert: Firestore.deleteField(),
    })
  } else if (objectType === 'signatures') {
    batch.update(SignaturesDoc(userId, assetId), {
      alert: Firestore.deleteField(),
    })
  }

  batch.set(
    HistoryDoc(
      userId,
      objectType,
      assetId,
      Firestore.doc(HistoryCollection(userId, objectType, assetId)).id
    ),
    {
      actionType: 'alertDone',
      createdAt: Firestore.serverTimestamp(),
      // Contemplar modo sustitucion
      ...(store.getters.isSubstituteMode
        ? {
            userRef: UsersDoc(store.state.userSubstitute.id),
            absentUserRef: UsersDoc(store.state.user.id),
          }
        : {
            userRef: UsersDoc(store.state.user.id),
          }),
      ...alert,
    },
    { merge: true }
  )

  return await batch.commit()
}

/**
 * Construye y devuelve una lista de objetos con todos los estados de una alerta, su color y su clave i18n.
 * Utilidad para poblar un selector
 *
 * @returns {Array<Object>} Un array de estados.
 */
export function buildAlertStatusList() {
  return [
    {
      id: 0,
      color: 'accept',
      icon: 'mdi-bell',
      status: 'alertStatus.pending',
    },
    {
      id: 1,
      color: 'warning',
      icon: 'mdi-bell-ring',
      status: 'alertStatus.urgent',
    },
    {
      id: 2,
      color: 'error',
      icon: 'mdi-bell-alert',
      status: 'alertStatus.expired',
    },
  ]
}

/**
 * Genera un archivo .ics y activa la descarga.
 * @param {Object} alert - El objeto de alerta {date, text, description}.
 * @param {Object} asset - El objeto de notificación o firma.
 * @param {string} objectType - 'notifications' o 'signatures'.
 */
export function downloadIcs(alert, asset, objectType = 'notifications') {
  const dateStr = moment(alert.date).format('YYYYMMDD')
  const startTime = `${dateStr}`
  const endTime = `${dateStr}`
  const summary = alert.text || 'Alerta GoodOk'
  const assetId = asset.id || asset['#Id'] || asset.identificador || ''
  const concept = asset.concepto || asset.purpose || ''
  const description = i18n.t('calendar.description', {
    assetId,
    concept,
    objectType:
      objectType === 'notifications'
        ? i18n.t('calendar.notification')
        : i18n.t('calendar.signature'),
  })

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//GoodOk//NONSGML v1.0//EN',
    'BEGIN:VEVENT',
    `DTSTART:${startTime}`,
    `DTEND:${endTime}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  saveAs(blob, `alerta_${assetId}.ics`)
}

/**
 * Genera un archivo .ics con múltiples eventos y activa la descarga.
 * @param {Object} alert - El objeto de alerta {date, text}.
 * @param {Array} assets - Lista de objetos de notificación o firma.
 * @param {string} objectType - 'notifications' o 'signatures'.
 */
export function downloadMassiveIcs(
  alert,
  assets,
  objectType = 'notifications'
) {
  const dateStr = moment(alert.date).format('YYYYMMDD')
  const startTime = `${dateStr}`
  const endTime = `${dateStr}`
  const summary = alert.text || 'Alerta GoodOk'

  const events = assets.map((asset) => {
    const assetId = asset.id || asset['#Id'] || asset.identificador || ''
    const concept = asset.concepto || asset.purpose || ''
    const description = i18n.t('calendar.description', {
      assetId,
      concept,
      objectType:
        objectType === 'notifications'
          ? i18n.t('calendar.notification')
          : i18n.t('calendar.signature'),
    })

    return [
      'BEGIN:VEVENT',
      `DTSTART:${startTime}`,
      `DTEND:${endTime}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      'END:VEVENT',
    ].join('\r\n')
  })

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//GoodOk//NONSGML v1.0//EN',
    ...events,
    'END:VCALENDAR',
  ].join('\r\n')

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  saveAs(blob, `alertas_masivas.ics`)
}
